import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { TranslateService } from '@ngx-translate/core';
import { CommunicationProtocol, ProtocolType } from 'icreate/common/models/communication-protocol';
import { FcuExternalCommunications } from 'icreate/common/models/fcu-unit';
import { communicationTypes } from 'icreate/common/models/fu-unit';
import { BaudRateOptions, Duplex, ModbusSetting, Port, RedundancyType } from 'icreate/common/models/port';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import {
    lanPortSettingValidator,
    portValidator,
    serialPortSettingValidator,
} from 'icreate/common/validators/port/port-validator';
import { ParityBit } from 'icreate/common/models/port';
import * as _ from 'lodash';
import { ModbusSettingsDialogComponent } from '@components/modbus-settings-dialog/modbus-settings-dialog.component';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-port-settings-dialog',
    templateUrl: './port-settings-dialog.component.html',
    styleUrl: './port-settings-dialog.component.css',
    standalone: false,
})
export class PortSettingsDialogComponent implements OnInit {
    readonly NAME_INPUT = 'name';
    readonly TIMEOUT_INPUT = 'timeout';
    readonly POLLING_INPUT = 'pollingInterval';
    readonly RETRY_INPUT = 'retry';
    readonly BAUD_RATE_INPUT = 'baudRate';
    readonly LOCAL_IP_INPUT = 'localIp';
    readonly REMOTE_IP_INPUT = 'remoteIp';
    readonly REMOTE_PORT_INPUT = 'remotePort';

    port: Port;

    initialPortData: Port;
    validationErrors: ValidationErrorMap = {};

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            port: Port;
            unitId: string;
            communicationProtocols: CommunicationProtocol[];
            commsList: FcuExternalCommunications[];
            isCeuUnit: boolean;
        },
        private dialogRef: MatDialogRef<PortSettingsDialogComponent>,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.port = this.data.port;
        this.initialPortData = _.cloneDeep(this.port);
    }

    closeDialog() {
        this.dialogRef.close();
    }

    save() {
        if (this.isSaveDisabled) return;
        this.dialogRef.close(this.port);
    }

    onInputChange({ value, name }) {
        if (name == this.NAME_INPUT) {
            this.port.name = value;
        } else if (name == this.LOCAL_IP_INPUT) {
            this.port.lanPortSetting.localIp = value;
        } else if (name == this.REMOTE_IP_INPUT) {
            this.port.lanPortSetting.remoteIp = value;
        } else if (name == this.REMOTE_PORT_INPUT) {
            this.port.lanPortSetting.remotePort = Number(value);
        } else if (name == this.TIMEOUT_INPUT && this.showExtraOptions) {
            this.port.timeout = Number(value);
        } else if (name == this.RETRY_INPUT && this.showExtraOptions) {
            this.port.retry = Number(value);
        } else if (name == this.POLLING_INPUT && this.showExtraOptions) {
            this.port.pollingInterval = Number(value);
        }
    }

    handleProtocolChanged({ value }) {
        const newProtocol = this.data.communicationProtocols.find(protocol => protocol.name == value);
        this.port.protocol = {
            protocolId: newProtocol.id,
            portIndex: this.port.port,
            communicationProtocol: newProtocol,
        };
        if (this.data.isCeuUnit) this.port.protocol.ceuUnitId = this.data.unitId;
        else this.port.protocol.extUnitId = this.data.unitId;
        this.cdr.detectChanges();
    }

    handleRedundacyUnitChanged({ value }) {
        this.port.redundancyUnitId = value;
    }

    handleRedundacyPortChanged({ value }) {
        this.port.redundancyPortIndex = value;
    }

    redundacyUpdated() {
        if (!this.port.redundancy) {
            this.port.redundancyUnitId = null;
            this.port.redundancyPortIndex = null;
            this.port.redundancyType = null;
        }
    }

    selectBaudRate(event) {
        this.port.serialPortSetting[this.BAUD_RATE_INPUT] = Number(event);
    }

    async openProtocolSettings() {
        if (
            this.port.protocol?.communicationProtocol &&
            (this.port.protocol.communicationProtocol.name == 'Standard Modbus (Master)' ||
                this.port.protocol.communicationProtocol.name == 'Standard Modbus (Slave)' ||
                this.port.protocol.communicationProtocol.name == 'Modbus TCP (Master)' ||
                this.port.protocol.communicationProtocol.name == 'Modbus TCP (Slave)')
        ) {
            const modbusSettings = this.port.protocol.standardModbus;
            const dialogRef = this.dialog.open(ModbusSettingsDialogComponent, {
                width: '85vw',
                data: {
                    port: this.port,
                    modbusSettings: modbusSettings,
                },
            });

            const updatedSettings: ModbusSetting[] = await lastValueFrom(dialogRef.afterClosed());
            if (updatedSettings) {
                this.port.protocol.standardModbus = updatedSettings;
            }
        }
    }

    get isSaveDisabled() {
        if (_.isEqual(this.initialPortData, this.data.port)) return true;
        if (this.portHasErrors) return true;
        return false;
    }

    get redundancyTypeOptions() {
        return [
            {
                value: RedundancyType.Primary,
                labelKey: 'port-settings.dialog.redundancy.type.primary',
            },
            {
                value: RedundancyType.Secondary,
                labelKey: 'port-settings.dialog.redundancy.type.secondary',
            },
        ];
    }

    get protocolOptions() {
        if (!this.data.communicationProtocols || this.data.communicationProtocols.length == 0) return [];
        const options: SimpleDropdownOption[] = this.data.communicationProtocols
            .filter(protocol => protocol.boardType == this.port.type)
            .map(protocol => {
                return { value: protocol.name, string: protocol.name };
            });
        return options;
    }

    get portProtocolName() {
        if (!this.data.communicationProtocols || this.data.communicationProtocols.length == 0) return null;
        if (!this.port.protocol) return null;

        const portProtocolId = this.port.protocol.protocolId;
        const portProtocol = this.data.communicationProtocols.find(protocol => protocol.id == portProtocolId);
        if (portProtocol) return portProtocol.name;
    }

    get protocolType() {
        if (!this.port?.protocol?.communicationProtocol) return;
        const portProtocolType = this.port.protocol.communicationProtocol.type;

        if (portProtocolType == ProtocolType.POLLING) return 'communication-protocols.type.polling';
        else if (portProtocolType == ProtocolType.RECEIVER) return 'communication-protocols.type.receiver';
        else if (portProtocolType == ProtocolType.TRANSMITER) return 'communication-protocols.type.transmiter';
        else if (portProtocolType == ProtocolType.RESPONDER) return 'communication-protocols.type.responder';

        return;
    }

    get redundancyUnitOptions() {
        let hasMultipleFcuUnit = this.data.commsList.length > 1;
        const options: SimpleDropdownOption[] = [];
        this.data.commsList.forEach(fcuUnit => {
            let initialString;
            if (hasMultipleFcuUnit) {
                initialString = this.translate.instant('dlg.FCU', { num: fcuUnit.fcuIndex });
            }
            const ceuUnits = fcuUnit.ceuUnits?.filter(unit => unit.commType == this.port.type);
            if (this.port.type == communicationTypes.serial) {
                const translateString = this.translate.instant('dlg.Ext-comm');

                options.push({
                    value: fcuUnit.extComms.id,
                    string: initialString ? `${initialString} - ${translateString}` : translateString,
                });
            }

            ceuUnits.forEach(unit => {
                const translateString = this.translate.instant('dlg.CEU', { num: unit.index });
                options.push({
                    value: unit.id,
                    string: initialString ? `${initialString} - ${translateString}` : translateString,
                });
            });
        });
        return options;
    }

    get redundancyIndexOptions() {
        if (!this.port.redundancyUnitId) return [];
        const options: SimpleDropdownOption[] = [];

        let unitPorts: Port[];
        for (let i = 0; i < this.data.commsList.length; i++) {
            const fcuUnit = this.data.commsList[i];
            if (fcuUnit.extComms.id == this.port.redundancyUnitId) {
                if (this.data.unitId == fcuUnit.extComms.id) {
                    unitPorts = fcuUnit.extComms.Ports.filter(port => port.port != this.data.port.port);
                } else unitPorts = fcuUnit.extComms.Ports;
                break;
            }
            const ceuUnit = fcuUnit.ceuUnits?.find(unit => unit.id == this.port.redundancyUnitId);
            if (ceuUnit) {
                if (this.data.unitId == ceuUnit.id) {
                    unitPorts = ceuUnit.Ports.filter(port => port.port != this.data.port.port);
                } else unitPorts = ceuUnit.Ports;
                break;
            }
        }

        if (unitPorts && unitPorts.length > 0) {
            unitPorts.forEach(port => {
                const translateString = this.translate.instant('dlg.port', { num: port.port });
                options.push({ value: port.port, string: translateString });
            });
        }

        return options;
    }

    get parityBitOptions() {
        const parityBitOptions = Object.values(ParityBit);
        const options: SimpleDropdownOption[] = [];
        parityBitOptions.forEach(option => {
            if (option !== ParityBit.UNDEFINED) {
                let string;
                if (option == ParityBit.None) string = 'serial-settings.parity-bit.none';
                else if (option == ParityBit.Even) string = 'serial-settings.parity-bit.even';
                else if (option == ParityBit.Odd) string = 'serial-settings.parity-bit.odd';
                if (string) options.push({ value: option, string });
            }
        });
        return options;
    }

    get stopBitOptions() {
        const options: SimpleDropdownOption[] = [
            { value: 1, string: '1' },
            { value: 1.5, string: '1.5' },
            { value: 2, string: '2' },
        ];

        return options;
    }

    get dataBitOptions() {
        const options: SimpleDropdownOption[] = [
            { value: 7, string: '7' },
            { value: 8, string: '8' },
        ];

        return options;
    }

    get duplexOptions() {
        const duplexOptions = Object.values(Duplex);
        const options: SimpleDropdownOption[] = [];
        duplexOptions.forEach(option => {
            if (option !== Duplex.UNDEFINED) {
                let string;
                if (option == Duplex.Full) string = 'serial-settings.duplex.full';
                else if (option == Duplex.Half) string = 'serial-settings.duplex.half';
                if (string) options.push({ value: option, string });
            }
        });
        return options;
    }

    get isSerialPort() {
        return this.port.type == communicationTypes.serial;
    }

    get isLanPort() {
        return this.port.type == communicationTypes.lan;
    }

    get showExtraOptions() {
        if (!this.port?.protocol?.communicationProtocol) return false;
        const portProtocolType = this.port.protocol.communicationProtocol.type;
        return (
            portProtocolType == ProtocolType.POLLING ||
            portProtocolType == ProtocolType.RECEIVER ||
            portProtocolType == ProtocolType.RESPONDER
        );
    }

    get baudRateOptionsArray() {
        return BaudRateOptions;
    }

    get hasProtocolDialog() {
        return (
            this.port.protocol?.communicationProtocol &&
            (this.port.protocol.communicationProtocol.name == 'Standard Modbus (Master)' ||
                this.port.protocol.communicationProtocol.name == 'Standard Modbus (Slave)' ||
                this.port.protocol.communicationProtocol.name == 'Modbus TCP (Master)' ||
                this.port.protocol.communicationProtocol.name == 'Modbus TCP (Slave)')
        );
    }

    get portHasErrors() {
        const errors = getValidationErrors(portValidator(), this.port);
        const mappedErrors = mapErrors(errors);
        return Object.keys(mappedErrors).length > 0;
    }

    get validatorFunction() {
        return portValidator;
    }

    get serialPortValidator() {
        return serialPortSettingValidator;
    }

    get lanPortValidator() {
        return lanPortSettingValidator;
    }
}
